import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { User } from 'oidc-client-ts'
import { userManager } from '@/auth/config'
import { resetTelemetryIdentity, startJourney } from '@/lib/telemetry'
import { reportBootstrapError } from '@/lib/bootstrapError'

interface OidcContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  isSigningOut: boolean
  user: User | null
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string>
}

const OidcContext = createContext<OidcContextValue | null>(null)

export function OidcProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSigningOut, setIsSigningOut] = useState(false)

  useEffect(() => {
    userManager.getUser().then((u) => {
      setUser(u)
      setIsLoading(false)
    }).catch((e: unknown) => {
      // A rejection here almost always means the PERSISTED user is unusable, not that
      // the app is broken: an expired token, or - the common one - a token minted against
      // a previous issuer (an IdP cutover) that no longer validates against
      // current metadata. That is a RECOVERABLE state every existing user hits on an issuer
      // change, so we must NOT render the fatal AppErrorScreen (which strands the user until
      // they manually clear cache). Instead: drop the poisoned stored user and fall through
      // unauthenticated, which routes to a fresh sign-in. removeUser() clears the stale entry
      // from storage so the next boot is clean even if this same error recurs.
      console.warn('[OidcProvider] stored user unusable, clearing and re-authenticating:', e)
      reportBootstrapError('oidc-init')
      userManager.removeUser().catch(() => { /* storage unavailable - still fall through */ })
        .finally(() => {
          setUser(null)
          setIsLoading(false)
        })
    })

    const onUserLoaded = (u: User) => setUser(u)
    const onUserUnloaded = () => setUser(null)
    // Surface silent-renew failures so a bad refresh-token rollout is observable
    // here rather than only via users reporting they were logged out. getAccessToken
    // still falls back to interactive signinSilent, so this is telemetry, not control flow.
    const onSilentRenewError = (err: Error) =>
      console.error('[OidcProvider] silent renew failed:', err)

    userManager.events.addUserLoaded(onUserLoaded)
    userManager.events.addUserUnloaded(onUserUnloaded)
    userManager.events.addSilentRenewError(onSilentRenewError)
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded)
      userManager.events.removeUserUnloaded(onUserUnloaded)
      userManager.events.removeSilentRenewError(onSilentRenewError)
    }
  }, [])

  const login = () => {
    // The `authentication` journey starts HERE and is minted into sessionStorage, which is what lets
    // it survive the full navigation away to Authentik and back (§7) - the one journey that has to.
    // Context propagation could not do this: the page is unloaded in between.
    startJourney('authentication')
    return userManager.signinRedirect()
  }

  const logout = () => {
    // Reset the anonymous tracking id at the auth boundary so tm_uid never
    // spans two different users signing in on the same device.
    resetTelemetryIdentity()
    // signoutRedirect() calls removeUser() BEFORE it navigates (oidc-client-ts
    // _signoutStart needs the id_token_hint first), which fires userUnloaded and
    // leaves AuthGuard looking at an unauthenticated session while the logout
    // navigation is still in flight. Without this latch the guard starts a fresh
    // signinRedirect in that gap: the user watches the bare "Loading…" spinner,
    // burns an auth attempt, and can be raced straight back into the IdP instead
    // of out of it. Only the failure path below clears it - on success the page
    // is replaced by Authentik.
    setIsSigningOut(true)
    return userManager
      .signoutRedirect()
      .catch((e: unknown) => {
        // end_session unreachable or not configured. The local session is already
        // gone, so land on the public signed-out page rather than bouncing back
        // into sign-in (which is what the signing-out latch exists to prevent).
        console.error('[auth] signoutRedirect failed:', e)
        return userManager.removeUser().catch(() => { /* storage unavailable */ }).finally(() => {
          setUser(null)
          window.location.replace('/signed-out')
        })
      })
  }

  const getAccessToken = async (): Promise<string> => {
    let u = await userManager.getUser()
    if (!u || u.expired) {
      u = await userManager.signinSilent()
    }
    if (!u?.access_token) throw new Error('No access token available')
    return u.access_token
  }

  // Note: a bootstrap getUser() rejection no longer renders a fatal screen here - a stale/
  // wrong-issuer stored token is recovered by clearing it and falling through to sign-in
  // (see the useEffect catch). Genuine config/discovery failures surface at login time via
  // main.tsx's callback error handling (the attempt-breaker), which holds its own loop guard.
  return (
    <OidcContext.Provider
      value={{
        isAuthenticated: !!user && !user.expired,
        isLoading,
        isSigningOut,
        user,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </OidcContext.Provider>
  )
}

export function useOidc(): OidcContextValue {
  const ctx = useContext(OidcContext)
  if (!ctx) throw new Error('useOidc must be used within OidcProvider')
  return ctx
}
